import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Users, FileText, Vote, Mic2, Loader2, X, ArrowRight } from 'lucide-react';
import { fetchApi } from '../../api/client';
import { useAppContext } from '../../context/AppContext';
import { partyMeta, factionMeta } from '../../lib/partyFactionMeta';
import { useT } from '../../i18n';

// ─── Result types ─────────────────────────────────────────────────────────────

interface MemberResult   { id: number; first_name: string; last_name: string; party: string; faction: string; }
interface BillResult     { id: number; short_number: string; title: string; business_type: string; status: string; }
interface VoteResult     { id: number; business_title: string; subject: string; date: string; }
interface SpeechResult   { id: string; speaker: string; business_title: string; meeting_date: string; }

interface SearchGroups {
  members:  MemberResult[];
  bills:    BillResult[];
  votes:    VoteResult[];
  speeches: SpeechResult[];
}

// ─── Flat navigable item ──────────────────────────────────────────────────────

interface NavItem { path: string; }

// ─── Scope hint tiles (shown when focused but no query) ───────────────────────

const SCOPE_TILES = [
  { icon: <Users  size={15} />, labelKey: 'nav.members',  path: '/members',  hint: 'omni.hint.members'  },
  { icon: <FileText size={15} />, labelKey: 'nav.bills',  path: '/bills',    hint: 'omni.hint.bills'    },
  { icon: <Vote   size={15} />, labelKey: 'nav.votes',    path: '/votes',    hint: 'omni.hint.votes'    },
  { icon: <Mic2   size={15} />, labelKey: 'nav.debates',  path: '/debates',  hint: 'omni.hint.debates'  },
] as const;

// ─── Section header ───────────────────────────────────────────────────────────

function GroupHeader({ icon, label, count }: { icon: React.ReactNode; label: string; count: number }) {
  return (
    <div className="flex items-center gap-[6px] px-[12px] py-[5px] bg-[var(--surface-2)] border-b border-[var(--border)]">
      <span className="text-[var(--text-3)]">{icon}</span>
      <span className="text-[11px] font-[family-name:var(--font-mono)] uppercase tracking-wide text-[var(--text-3)] font-semibold">
        {label}
      </span>
      <span className="ml-auto text-[11px] font-[family-name:var(--font-mono)] text-[var(--text-3)]">{count}</span>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function OmniSearch() {
  const { lang } = useAppContext();
  const t = useT();
  const navigate = useNavigate();

  const [query, setQuery]       = useState('');
  const [groups, setGroups]     = useState<SearchGroups | null>(null);
  const [loading, setLoading]   = useState(false);
  const [open, setOpen]         = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef     = useRef<HTMLInputElement>(null);
  const timerRef     = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Global ⌘K / Ctrl+K shortcut ──────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // ── Close on outside click ────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setActiveIdx(-1);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Build flat nav list from groups (for keyboard nav) ────────────────────
  const navItems = useMemo<NavItem[]>(() => {
    if (!groups) return [];
    const items: NavItem[] = [];
    groups.members.forEach(m  => items.push({ path: `/members/${m.id}`   }));
    groups.bills.forEach(b    => items.push({ path: `/bills/${b.id}`     }));
    groups.votes.forEach(v    => items.push({ path: `/votes/${v.id}`     }));
    groups.speeches.forEach(s => items.push({ path: `/debates/${s.id}`   }));
    return items;
  }, [groups]);

  // Reset active index when results change
  useEffect(() => { setActiveIdx(-1); }, [groups]);

  const runSearch = useCallback(async (q: string) => {
    if (q.length < 2) { setGroups(null); return; }
    setLoading(true);
    try {
      const [membersRes, billsRes, votesRes, speechesRes] = await Promise.allSettled([
        fetchApi<{ results: MemberResult[] }>('/v1/councillors',    { name: q, top: 4, language: lang }),
        fetchApi<{ results: BillResult[] }>('/v1/affairs/search',   { keyword: q, top: 4, language: lang }),
        fetchApi<{ results: VoteResult[] }>('/v1/votes',            { keyword: q, top: 4, language: lang }),
        fetchApi<{ results: SpeechResult[] }>('/v1/transcripts',    { search_text: q, top: 4 }),
      ]);
      setGroups({
        members:  membersRes.status  === 'fulfilled' ? membersRes.value.results  ?? [] : [],
        bills:    billsRes.status    === 'fulfilled' ? billsRes.value.results    ?? [] : [],
        votes:    votesRes.status    === 'fulfilled' ? votesRes.value.results    ?? [] : [],
        speeches: speechesRes.status === 'fulfilled' ? speechesRes.value.results ?? [] : [],
      });
    } finally {
      setLoading(false);
    }
  }, [lang]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    setActiveIdx(-1);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!val.trim()) { setGroups(null); return; }
    timerRef.current = setTimeout(() => runSearch(val.trim()), 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    if (e.key === 'Escape') {
      setOpen(false);
      setActiveIdx(-1);
      inputRef.current?.blur();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, navItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIdx >= 0 && navItems[activeIdx]) {
        go(navItems[activeIdx].path);
      } else if (navItems.length > 0) {
        go(navItems[0].path);
      }
    }
  };

  const go = (path: string) => {
    setOpen(false);
    setQuery('');
    setGroups(null);
    setActiveIdx(-1);
    navigate(path);
  };

  const clearQuery = () => {
    setQuery('');
    setGroups(null);
    setActiveIdx(-1);
    inputRef.current?.focus();
  };

  const hasResults = groups && (
    groups.members.length + groups.bills.length + groups.votes.length + groups.speeches.length > 0
  );

  // Compute per-group flat index offsets for keyboard highlight
  const memberOffset  = 0;
  const billOffset    = (groups?.members.length  ?? 0);
  const voteOffset    = billOffset  + (groups?.bills.length    ?? 0);
  const speechOffset  = voteOffset  + (groups?.votes.length    ?? 0);

  const showDropdown = open && query.length >= 2;
  const showHint     = open && query.length < 2;

  return (
    <div className="relative w-full" ref={containerRef}>
      {/* Input */}
      <div className="relative w-full">
        <Search size={15} className="absolute left-[12px] top-1/2 -translate-y-1/2 text-[var(--text-3)] pointer-events-none" />

        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={t('omni.placeholder')}
          autoComplete="off"
          className="w-full bg-[var(--surface)] text-[var(--text-1)] border border-[var(--border)] rounded-[var(--r2)] font-[family-name:var(--font-display)] text-[13px] py-[8px] pl-[36px] pr-[72px] placeholder:text-[var(--text-3)] focus:outline-none focus:border-[var(--accent)] focus:ring-[2px] focus:ring-[var(--accent-soft)] transition-all"
        />

        <div className="absolute right-[10px] top-1/2 -translate-y-1/2 flex items-center gap-[6px] pointer-events-none">
          {loading && (
            <Loader2 size={13} className="text-[var(--text-3)] animate-spin pointer-events-none" />
          )}
          {query && !loading && (
            <button
              onMouseDown={e => { e.preventDefault(); clearQuery(); }}
              className="pointer-events-auto text-[var(--text-3)] hover:text-[var(--text-1)] transition-colors"
              tabIndex={-1}
            >
              <X size={13} />
            </button>
          )}
          {!query && (
            <kbd className="hidden sm:inline-flex items-center gap-[2px] px-[5px] py-[2px] text-[11px] font-[family-name:var(--font-mono)] text-[var(--text-3)] border border-[var(--border)] rounded-[3px] bg-[var(--surface-2)] leading-none">
              ⌘K
            </kbd>
          )}
        </div>
      </div>

      {/* Scope hint — shown when focused but no query */}
      {showHint && (
        <div className="absolute top-[calc(100%+4px)] left-0 right-0 bg-[var(--surface)] border border-[var(--border)] rounded-[var(--r2)] shadow-xl z-50 overflow-hidden">
          <div className="px-[12px] pt-[10px] pb-[4px]">
            <p className="text-[11px] font-[family-name:var(--font-mono)] text-[var(--text-3)] uppercase tracking-wide mb-[8px]">
              {t('omni.scopeLabel')}
            </p>
            <div className="grid grid-cols-2 gap-[6px] pb-[10px]">
              {SCOPE_TILES.map(tile => (
                <button
                  key={tile.path}
                  onMouseDown={e => { e.preventDefault(); go(tile.path); }}
                  className="flex items-center gap-[8px] px-[10px] py-[8px] rounded-[var(--r1)] border border-[var(--border)] bg-[var(--surface-2)] hover:border-[var(--accent)] hover:bg-[var(--accent-soft)] transition-colors text-left group"
                >
                  <span className="text-[var(--text-3)] group-hover:text-[var(--accent)] transition-colors shrink-0">
                    {tile.icon}
                  </span>
                  <div className="min-w-0">
                    <p className="text-[12px] font-medium text-[var(--text-1)] leading-tight">{t(tile.labelKey)}</p>
                    <p className="text-[11px] text-[var(--text-3)] leading-tight truncate">{t(tile.hint)}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Results dropdown */}
      {showDropdown && (
        <div className="absolute top-[calc(100%+4px)] left-0 right-0 bg-[var(--surface)] border border-[var(--border)] rounded-[var(--r2)] shadow-xl z-50 overflow-hidden max-h-[480px] overflow-y-auto">

          {/* Loading skeleton */}
          {loading && !groups && (
            <div className="px-[12px] py-[14px] text-[12px] text-[var(--text-3)] font-[family-name:var(--font-mono)] text-center flex items-center justify-center gap-[6px]">
              <Loader2 size={12} className="animate-spin" />
              {t('omni.searching')}
            </div>
          )}

          {/* No results */}
          {!loading && groups && !hasResults && (
            <div className="px-[12px] py-[14px] text-center">
              <p className="text-[13px] text-[var(--text-2)]">{t('omni.noResults')}</p>
              <p className="text-[11px] text-[var(--text-3)] mt-[3px] font-[family-name:var(--font-mono)]">"{query}"</p>
            </div>
          )}

          {/* Members */}
          {(groups?.members.length ?? 0) > 0 && (
            <section>
              <GroupHeader icon={<Users size={11} />} label={t('nav.members')} count={groups!.members.length} />
              {groups!.members.map((m, i) => {
                const idx = memberOffset + i;
                return (
                  <ResultRow key={m.id} active={activeIdx === idx} onClick={() => go(`/members/${m.id}`)}>
                    <span className="text-[13px] font-medium text-[var(--text-1)]">
                      {m.first_name} {m.last_name}
                    </span>
                    <span className="text-[11px] font-[family-name:var(--font-mono)] text-[var(--text-3)]">
                      {[factionMeta(m.faction).abbr, partyMeta(m.party).abbr].filter(Boolean).join(' · ')}
                    </span>
                  </ResultRow>
                );
              })}
              <ViewAll label={t('nav.members')} onClick={() => go(`/members?name=${encodeURIComponent(query)}`)} />
            </section>
          )}

          {/* Bills */}
          {(groups?.bills.length ?? 0) > 0 && (
            <section className={(groups?.members.length ?? 0) > 0 ? 'border-t border-[var(--border)]' : ''}>
              <GroupHeader icon={<FileText size={11} />} label={t('nav.bills')} count={groups!.bills.length} />
              {groups!.bills.map((b, i) => {
                const idx = billOffset + i;
                return (
                  <ResultRow key={b.id} active={activeIdx === idx} onClick={() => go(`/bills/${b.id}`)}>
                    <div className="flex items-center gap-[6px]">
                      <span className="text-[11px] font-[family-name:var(--font-mono)] text-[var(--accent)] shrink-0">{b.short_number}</span>
                      <span className="text-[11px] font-[family-name:var(--font-mono)] text-[var(--text-3)] shrink-0">{b.business_type}</span>
                    </div>
                    <span className="text-[12px] text-[var(--text-1)] line-clamp-1">{b.title}</span>
                  </ResultRow>
                );
              })}
              <ViewAll label={t('nav.bills')} onClick={() => go(`/bills?q=${encodeURIComponent(query)}`)} />
            </section>
          )}

          {/* Votes */}
          {(groups?.votes.length ?? 0) > 0 && (
            <section className={(groups?.members.length ?? 0) + (groups?.bills.length ?? 0) > 0 ? 'border-t border-[var(--border)]' : ''}>
              <GroupHeader icon={<Vote size={11} />} label={t('nav.votes')} count={groups!.votes.length} />
              {groups!.votes.map((v, i) => {
                const idx = voteOffset + i;
                return (
                  <ResultRow key={v.id} active={activeIdx === idx} onClick={() => go(`/votes/${v.id}`)}>
                    <span className="text-[12px] text-[var(--text-1)] line-clamp-1">{v.business_title || v.subject}</span>
                    {v.date && (
                      <span className="text-[11px] font-[family-name:var(--font-mono)] text-[var(--text-3)]">{v.date.slice(0, 10)}</span>
                    )}
                  </ResultRow>
                );
              })}
              <ViewAll label={t('nav.votes')} onClick={() => go(`/votes?q=${encodeURIComponent(query)}`)} />
            </section>
          )}

          {/* Speeches */}
          {(groups?.speeches.length ?? 0) > 0 && (
            <section className={navItems.length - (groups?.speeches.length ?? 0) > 0 ? 'border-t border-[var(--border)]' : ''}>
              <GroupHeader icon={<Mic2 size={11} />} label={t('nav.debates')} count={groups!.speeches.length} />
              {groups!.speeches.map((s, i) => {
                const idx = speechOffset + i;
                return (
                  <ResultRow key={s.id} active={activeIdx === idx} onClick={() => go(`/debates/${s.id}`)}>
                    <span className="text-[12px] text-[var(--text-1)]">{s.speaker}</span>
                    <span className="text-[11px] font-[family-name:var(--font-mono)] text-[var(--text-3)] line-clamp-1">{s.business_title}</span>
                  </ResultRow>
                );
              })}
              <ViewAll label={t('nav.debates')} onClick={() => go(`/debates?q=${encodeURIComponent(query)}`)} />
            </section>
          )}

          {/* Keyboard nav hint */}
          {hasResults && (
            <div className="flex items-center gap-[10px] px-[12px] py-[6px] border-t border-[var(--border)] bg-[var(--surface-2)]">
              <KbdHint keys={['↑', '↓']} label={t('omni.kbdNav')} />
              <KbdHint keys={['↵']} label={t('omni.kbdOpen')} />
              <KbdHint keys={['Esc']} label={t('omni.kbdClose')} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ResultRow({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (active) ref.current?.scrollIntoView({ block: 'nearest' });
  }, [active]);
  return (
    <button
      ref={ref}
      onClick={onClick}
      className={`w-full text-left px-[12px] py-[8px] flex flex-col gap-[2px] transition-colors ${
        active ? 'bg-[var(--accent-soft)]' : 'hover:bg-[var(--surface-2)]'
      }`}
    >
      {children}
    </button>
  );
}

function ViewAll({ label, onClick }: { label: string; onClick: () => void }) {
  const t = useT();
  return (
    <button
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      className="w-full flex items-center justify-end gap-[4px] px-[12px] py-[5px] text-[11px] font-[family-name:var(--font-mono)] text-[var(--text-3)] hover:text-[var(--accent)] transition-colors border-t border-[var(--border)] bg-[var(--surface)]"
    >
      {t('omni.viewAll')} {label} <ArrowRight size={9} />
    </button>
  );
}

function KbdHint({ keys, label }: { keys: string[]; label: string }) {
  return (
    <span className="flex items-center gap-[3px] text-[11px] font-[family-name:var(--font-mono)] text-[var(--text-3)]">
      {keys.map(k => (
        <kbd key={k} className="px-[4px] py-[1px] border border-[var(--border)] rounded-[3px] bg-[var(--surface)] text-[var(--text-3)] leading-none">
          {k}
        </kbd>
      ))}
      <span className="ml-[2px]">{label}</span>
    </span>
  );
}
